/**
 * component will provide zoom facility for keyBoard users
 *
 * @module views/components/ZoomController
 * @memberof -Common
 */
import './ZoomController.scss';

import React, { useCallback, useEffect, useReducer, useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';

import { constants } from '@ulta/utils/constants/constants';

const { ESC_KEY, UPARROW_KEY, DOWNARROW_KEY, LEFTARROW_KEY, RIGHTARROW_KEY, PLUS_KEY, MINNUS_KEY } = constants;

/**
 * Represents a ZoomController component
 *
 * @method
 * @param {ZoomControllerProps} props - React properties passed from composition
 * @returns ZoomController
 */
export const ZoomController = function( props ){
  const initialState = {
    scale: 1,
    x: 0,
    y: 0,
    style: {}
  };

  const [state, dispatch] = useReducer( zoomReducer, initialState );
  const closeRef = useRef();
  const moduleRef = useRef();

  const {
    mediaContent,
    show,
    zoomPlusIconAccessibilityLabel,
    zoomMinusIconAccessibilityLabel,
    moveUpIconAccessibilityLabel,
    moveDownIconAccessibilityLabel,
    moveLeftIconAccessibilityLabel,
    moveRightIconAccessibilityLabel,
    closeAccessibilityLabel,
    screenReaderAccessibilityLabel,
    moveLegendAccessibilityLabel } = props;
  const src = mediaContent?.imageUrl?.split( '?' )?.[0];
  const tabIndex = show ? 0 : -1;

  useEffect( () => {
    dispatch( { type: ZOOM_ACTIONS.RESET } );
  }, [src, show] );

  useEffect( () => {
    if( !show ){
      return;
    }
    const button = document.querySelector( '.ZoomController__iconClose button' );
    button?.focus();
  }, [show] );

  const onClose = () => {
    props.onClose && props.onClose();
  };

  const onKeyPress = useCallback( composeKeyPressHandler( { show }, { dispatch, onClose } ), [show] );
  UseKeyPress( ZOOM_ADA_KEYCODES, moduleRef, onKeyPress, [] );

  if( !src ){
    return null;
  }

  return (
    <div
      className={ classNames( 'ZoomController', { 'ZoomController--show': show } ) }
      ref={ moduleRef }
      role='region'
      aria-label={ screenReaderAccessibilityLabel }
    >
      <p className='sr-only'>{ moveLegendAccessibilityLabel }</p>
      <div className='ZoomController__mainImageWrapper'>
        <img
          className='ZoomController__mainImage'
          src={ `${src}?w=1080&h=1080&fmt=auto` }
          alt={ mediaContent?.altText || '' }
          style={ state.style }
        />
      </div>
      <div className='ZoomController__icons'>
        <div className='ZoomController__iconClose'>
          <Button
            icon
            iconImage='X'
            iconSize='s'
            className='ZoomController__iconClose__icon'
            onClick={ onClose }
            ref={ closeRef }
            tabIndex={ tabIndex }
            ariaLabel={ closeAccessibilityLabel }
            ariaHidden={ !show }
          />
        </div>
        <div className={
          classNames( 'ZoomController__iconsBottom', {
            'ZoomController__iconsBottom--hide': !show
          } )
        }
        >
          <div className='ZoomController__iconsBottomLeft'>
            <Button
              icon
              iconImage='Add'
              iconSize='m'
              className=''
              onClick={ () => dispatch( { type: ZOOM_ACTIONS.ZOOM_IN } ) }
              ariaLabel={ zoomPlusIconAccessibilityLabel }
              ariaHidden={ !show }
            />
            <Button
              icon
              iconImage='Minus'
              iconSize='m'
              className=''
              onClick={ () => dispatch( { type: ZOOM_ACTIONS.ZOOM_OUT } ) }
              ariaLabel={ zoomMinusIconAccessibilityLabel }
              ariaHidden={ !show }
            />
          </div>
          <div className='ZoomController__iconsBottomRight'>
            <Button
              icon
              iconImage='CaretBack'
              iconSize='s'
              className='ZoomController__caretBackButton'
              onClick={ () => dispatch( { type: ZOOM_ACTIONS.PAN_LEFT } ) }
              ariaLabel={ moveLeftIconAccessibilityLabel }
              ariaHidden={ !show }
            />
            <Button
              icon
              iconImage='CaretDown'
              iconSize='s'
              className='ZoomController__caretDown'
              onClick={ () => dispatch( { type: ZOOM_ACTIONS.PAN_DOWN } ) }
              ariaLabel={ moveDownIconAccessibilityLabel }
              ariaHidden={ !show }
            />
            <Button
              icon
              iconImage='CaretForward'
              iconSize='s'
              className='ZoomController__caretForward'
              onClick={ () => dispatch( { type: ZOOM_ACTIONS.PAN_RIGHT } ) }
              ariaLabel={ moveRightIconAccessibilityLabel }
              ariaHidden={ !show }
            />
            <Button
              icon
              iconImage='CaretUp'
              iconSize='s'
              className='ZoomController__caretUp'
              onClick={ () => dispatch( { type: ZOOM_ACTIONS.PAN_UP } ) }
              ariaLabel={ moveUpIconAccessibilityLabel }
              ariaHidden={ !show }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// constant value for used for ADA users
export const ZOOM_ADA_KEYCODES = [
  ESC_KEY,
  UPARROW_KEY,
  DOWNARROW_KEY,
  LEFTARROW_KEY,
  RIGHTARROW_KEY,
  PLUS_KEY,
  MINNUS_KEY
];

/**
 * Represents a composeKeyPressHandler method,which calls dispatch method on key press (+,-, esc, leftarrow etc)
 *
 * @method
 * @param {object} methods, - contains dispatch and onclose  method
 * @param {object} event, - Contains the information about event keyCode
 */

export const composeKeyPressHandler = ( data, methods ) => ( event ) => {
  const { show } = data || {};
  const { dispatch, onClose } = methods || {};

  if( !show ){
    return;
  }

  switch ( event.keyCode ){
    case UPARROW_KEY:
      dispatch( { type: ZOOM_ACTIONS.PAN_UP } );
      break;
    case DOWNARROW_KEY:
      dispatch( { type: ZOOM_ACTIONS.PAN_DOWN } );
      break;
    case LEFTARROW_KEY:
      dispatch( { type: ZOOM_ACTIONS.PAN_LEFT } );
      break;
    case RIGHTARROW_KEY:
      dispatch( { type: ZOOM_ACTIONS.PAN_RIGHT } );
      break;
    case PLUS_KEY:
      dispatch( { type: ZOOM_ACTIONS.ZOOM_IN } );
      break;
    case MINNUS_KEY:
      dispatch( { type: ZOOM_ACTIONS.ZOOM_OUT } );
      break;
    case ESC_KEY:
      onClose();
      break;
  }
};

/**
 * Represents a zoomReducer method
 *
 * @method
 * @param {object} state, - curremt state of style
 * @param {object} action, - information about the action to be performed
 * return the new sate of style
 */
export const zoomReducer = ( state, action ) => {
  let { scale, x, y, style } = { ...state };

  // const panStep = ZOOM_OPTIONS.panMultipler / scale
  const panStep = ZOOM_OPTIONS.panMultipler;

  switch ( action.type ){
    case ZOOM_ACTIONS.ZOOM_IN:
      scale = Math.min( scale + ZOOM_OPTIONS.zoomStep, ZOOM_OPTIONS.maxZoom );
      break;
    case ZOOM_ACTIONS.ZOOM_OUT:
      scale = Math.max( scale - ZOOM_OPTIONS.zoomStep, 1 );
      break;
    case ZOOM_ACTIONS.PAN_LEFT:
      x = x - panStep;
      if( x === 0 ){
        x = x * -1;
      }
      x = Math.max( x, ZOOM_OPTIONS.maxPan * -1 );
      break;
    case ZOOM_ACTIONS.PAN_RIGHT:
      x = x + panStep;
      x = Math.min( x, ZOOM_OPTIONS.maxPan );
      break;
    case ZOOM_ACTIONS.PAN_UP:
      y = y - panStep;
      if( y === 0 ){
        y = y * -1;
      }
      y = Math.max( y, ZOOM_OPTIONS.maxPan * -1 );
      break;
    case ZOOM_ACTIONS.PAN_DOWN:
      y = y + panStep;
      y = Math.min( y, ZOOM_OPTIONS.maxPan );
      break;
    case ZOOM_ACTIONS.RESET:
      scale = 1;
      x = 0;
      y = 0;
      break;
  }

  const xPer = `${x * 100}%`;
  const yPer = `${y * 100}%`;

  style = {
    transform: `scale(${scale}) translate(${xPer}, ${yPer})`
  };
  return { scale, x, y, style };
};

// Default value for zoom parameters
export const ZOOM_OPTIONS = {
  maxZoom: 2.5,
  zoomStep: 0.5,
  panMultipler: 0.1,
  maxPan: 0.25
};

// Constant value used in reduser function
export const ZOOM_ACTIONS = {
  ZOOM_IN: 'ZOOM_IN',
  ZOOM_OUT: 'ZOOM_OUT',
  PAN_LEFT: 'PAN_LEFT',
  PAN_RIGHT: 'PAN_RIGHT',
  PAN_UP: 'PAN_UP',
  PAN_DOWN: 'PAN_DOWN',
  RESET: 'RESET'
};

/**
 * Property type definitions
 * @typedef ZoomControllerProps
 * @type {object}
 * @property {boolean} show - Display the component
 * @property {function} onClose - On close callback
 * @property {object} mediaContent - Sets the media data
 * @property {string} zoomPlusIconAccessibilityLabel - Set the value for ariaLabel of plus icon
 * @property {string} zoomMinusIconAccessibilityLabel - Set the value for ariaLabel of minus icon
 * @property {string} moveUpIconAccessibilityLabel - Set the value for ariaLabel of zoom up icon
 * @property {string} moveDownIconAccessibilityLabel - Set the value for ariaLabel of zoom down icon
 * @property {string} moveLeftIconAccessibilityLabel - Set the value for ariaLabel of zoom left icon
 * @property {string} moveRightIconAccessibilityLabel - Set the value for ariaLabel of zoom right icon
 * @property {string} closeAccessibilityLabel -Set the value for ariaLabel of zoom close icon
 * @property {string} screenReaderAccessibilityLabel -Set the value for ariaLabel of image.
 * @property {string} moveLegendAccessibilityLabel -Set the value for sr-only class of image.
 */
export const propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  mediaContent: PropTypes.object.isRequired,
  zoomPlusIconAccessibilityLabel: PropTypes.string,
  zoomMinusIconAccessibilityLabel: PropTypes.string,
  moveUpIconAccessibilityLabel: PropTypes.string,
  moveDownIconAccessibilityLabel: PropTypes.string,
  moveLeftIconAccessibilityLabel: PropTypes.string,
  moveRightIconAccessibilityLabel: PropTypes.string,
  closeAccessibilityLabel: PropTypes.string,
  screenReaderAccessibilityLabel: PropTypes.string,
  moveLegendAccessibilityLabel: PropTypes.string
};

ZoomController.propTypes = propTypes;

export default ZoomController;
